import { memo, useCallback, useRef, useState } from 'react';
import {
  faPenToSquare,
  faTrashXmark,
} from '@fortawesome/pro-regular-svg-icons';

import { AdminSection, Cluster } from '@typings';
import { removeClusterOrganization } from '@services';
import { toastifyResponseError } from '@utils';

import { Button, Icon, Modal, ModalRef } from '@components';
import { ManageClusterOrganizationModal } from '@components/Cluster';

type Props = {
  clusterName: Cluster.Model['name'];
  org: AdminSection.ClusterOrganization;
  onOrgUpdate?: (org: AdminSection.ClusterOrganization) => void;
  onOrgRemove?: (orgName: AdminSection.ClusterOrganization['orgName']) => void;
};

export const AdminSectionClusterOrganizationActions = memo<Props>(
  ({ clusterName, org, onOrgRemove, onOrgUpdate }) => {
    const editModalRef = useRef<ModalRef>(null);
    const removeModalRef = useRef<ModalRef>(null);

    const [isRemoving, setRemoving] = useState(false);

    const handleRemove = useCallback(async () => {
      setRemoving(true);

      try {
        await removeClusterOrganization({ orgName: org.orgName, clusterName });

        onOrgRemove?.(org.orgName);
        removeModalRef.current?.close();
      } catch (error) {
        toastifyResponseError(error);
      } finally {
        setRemoving(false);
      }
    }, [clusterName, onOrgRemove, org.orgName]);

    const removeModalContent = (
      <Modal.Content title="Remove Organization" className="w-[520px]">
        <p className="flex flex-wrap items-center justify-center gap-1">
          Are you sure you want to remove the organization
          <b>
            {org.orgName}
            <span className="font-normal">?</span>
          </b>
        </p>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => removeModalRef.current?.close()}
          >
            Cancel
          </Button>
          <Button
            variant="error"
            loading={isRemoving}
            className="px-14"
            onClick={handleRemove}
          >
            Remove
          </Button>
        </Modal.Footer>
      </Modal.Content>
    );

    return (
      <>
        <Modal
          ref={editModalRef}
          content={
            <ManageClusterOrganizationModal
              org={org}
              clusterName={clusterName}
              onOrgUpdate={onOrgUpdate}
            />
          }
        >
          <Button variant="ghost" className="h-6 w-6 p-0 text-[18px]">
            <Icon icon={faPenToSquare} />
          </Button>
        </Modal>

        <span className="h-4 border-l" />

        <Modal ref={removeModalRef} content={removeModalContent}>
          <Button variant="ghost-error" className="h-6 w-6 p-0 text-[18px]">
            <Icon icon={faTrashXmark} />
          </Button>
        </Modal>
      </>
    );
  },
);

AdminSectionClusterOrganizationActions.displayName =
  'AdminSectionClusterOrganizationActions';
